import { Checkbox, ProgressIndicator, RadioButton, Text, TextLink } from '@oneaudi/unified-web-components';
import { htmlToText } from 'html-to-text';
import { MouseEvent, useCallback, useMemo } from 'react';
import { useI18n } from '~/app/i18n/i18n';
import { Prices } from '~/components/prices';
import { CardContent } from '~/components/selection-card-new/card-content';
import { SelectionCardProps } from '~/components/selection-card-new/models/selection-card-props';
import { SelectionCardStyles } from '~/components/selection-card-new/styles/selection-card-styles';
import { DATA_TEST_ID } from '~/test/data-test-id';

export const hasTeaserText = (teaserText: string | null | undefined) => teaserText && teaserText.trim();

export const SelectionCard = ({
	item: {
		selected,
		readonly,
		loading,
		onSelect,
		name,
		imgUrl,
		price,
		textLink,
		standard,
		incentive,
		onDescriptionToggle,
		disclaimerContent,
		disclaimerHeadline,
		disclaimerStartDate,
		disclaimerEndDate,
		key,
	},
	withImage = true,
	type = 'checkbox',
	children,
	id,
}: SelectionCardProps) => {
	const disabled = useMemo(() => readonly || loading, [loading, readonly]);
	const { i18n } = useI18n();

	const onClick = useCallback(() => {
		if (readonly) return;
		onSelect();
	}, [readonly, onSelect]);

	const onTextLinkClick = useCallback(
		(event: MouseEvent) => {
			event.stopPropagation();
			event.preventDefault();
			if (!textLink) return;
			textLink.onTextLinkClick();
		},
		[textLink],
	);

	return (
		<SelectionCardStyles
			data-testid={DATA_TEST_ID.SELECTION_CARD_CONTAINER}
			hasTechData={true}
			className={`${disabled ? 'is-readonly' : ''} ${selected ? 'is-selected' : ''}`}>
			{imgUrl && withImage && (
				<div className="image-container">
					<img src={imgUrl} alt={name || ''} loading="lazy" />
				</div>
			)}
			<div className="selection-card-content">
				<div className="selection-card-header">
					<div className="selection-card-title">
						{type === 'checkbox' && (
							<Checkbox
								label={htmlToText(name || '')}
								checked={selected}
								id=""
								name=""
								value=""
								disabled={disabled}
								onChange={() => ({})}
								onClick={onClick}
								className="top-item"
								data-testid={DATA_TEST_ID.SELECTION_CARD_CHECKBOX}
								aria-describedby={`${id}-price-information`}
							/>
						)}
						{type === 'radio' && (
							<RadioButton
								label={htmlToText(name || '')}
								checked={selected}
								id=""
								name=""
								value=""
								disabled={disabled}
								onClick={onClick}
								onChange={() => ({})}
								className="top-item"
								data-testid={DATA_TEST_ID.SELECTION_CARD_CHECKBOX}
								aria-describedby={`${id}-price-information`}
							/>
						)}
						{loading ? (
							<ProgressIndicator className="loading-item" variant={'spinner'} type={'infinite'} />
						) : null}
					</div>
					{incentive}
					{price &&
						(standard ? (
							<Text tag="p" variant={{ type: 'copy', order: '2', style: 'normal' }}>
								{i18n('ngc.common.standard')}
							</Text>
						) : (
							<Prices
								id={`${id}-price-information`}
								totalPrice={price.totalPrice}
								pr7={key}
								vatConfig={price.feature}
							/>
						))}
				</div>
				<CardContent
					disclaimerHeadline={disclaimerHeadline}
					disclaimerContent={disclaimerContent}
					disclaimerStartDate={disclaimerStartDate}
					disclaimerEndDate={disclaimerEndDate}
					alwaysVisible={selected}
					onDescriptionToggle={onDescriptionToggle}
					linksSection={
						textLink ? (
							<TextLink size="sm" variant="standalone" onClick={onTextLinkClick}>
								{textLink.label}
							</TextLink>
						) : null
					}>
					{children}
				</CardContent>
			</div>
		</SelectionCardStyles>
	);
};
